import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import {
  firebaseAuth,
  firebaseDB,
  firebaseStorage,
  firebaseFunctions,
  workoutCollection,
  COLLECTIONS,
} from "./firebaseApp";
import { httpsCallable } from "firebase/functions";
import { resizeForUpload, uploadFailureReason } from "../utils/imageResize";
import { trackImageEvent } from "../utils/imageAnalytics";
import { IMAGE_EVENTS } from "../constants/imageAnalyticsEvents";
import { TYPE as PRODUCT_TYPE } from "../constants/ProductConst";

/*
 * Resize an image for its upload surface, upload it, and return the download
 * URL. The storage path is built AFTER the resize because a HEIC source is
 * transcoded to JPEG — a path keyed on the file name must use the resized
 * file's name, never the original's.
 *
 * Every upload surface inherits its analytics from this single choke-point:
 * one resized/succeeded/failed event set, `surface` enum only, no filenames.
 */
export const resizeAndUpload = async (file, surface, buildPath) => {
  try {
    const resized = await resizeForUpload(file, surface);
    if (resized.wasResized) {
      trackImageEvent(IMAGE_EVENTS.resized, {
        surface,
        bytes_before: resized.bytesBefore,
        bytes_after: resized.bytesAfter,
        duration_ms: Math.round(resized.durationMs),
      });
    }
    const storageRef = ref(firebaseStorage, buildPath(resized.file));
    const snapshot = await uploadBytes(storageRef, resized.file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    trackImageEvent(IMAGE_EVENTS.uploadSucceeded, { surface });
    return downloadURL;
  } catch (error) {
    trackImageEvent(IMAGE_EVENTS.uploadFailed, {
      surface,
      reason: uploadFailureReason(error),
    });
    throw error;
  }
};

/*
 * Uploading programming cover art
 */
export const uploadCoverArt = async (file, userUID, productUID = null) => {
  /* -------- [preCheck] --- */
  if (!file) {
    return "";
  }
  /* ----------------------- */
  return resizeAndUpload(
    file,
    "coverArt",
    (resizedFile) => `users/${userUID}/CoverArts/${productUID ? productUID : resizedFile.name}`
  );
};

/*
 * Creating new workout card
 */
export const newWorkoutCards = async (workoutCardData) => {
  const newWorkoutCard = httpsCallable(firebaseFunctions, "createNewWorkoutCard");
  const ret = await newWorkoutCard(workoutCardData);
  return ret;
};

/*
 * Creating new workout card
 */
export const updateWorkoutCard_Backend = async (card) => {
  const update = httpsCallable(firebaseFunctions, "updateWorkoutCard");
  const ret = await update(card);
  return ret;
};

/*
 * Delete workout card
 */
export const deleteWorkoutCard = async (cardUID) => {
  const docRef = doc(firebaseDB, COLLECTIONS.WorkoutCards, cardUID);
  return await deleteDoc(docRef);
};

/*
 * Delete workout card from programming
 */
export const deleteWorkoutFromProgramming = async (card) => {
  const deleteWC = httpsCallable(firebaseFunctions, "deleteWorkoutCardFromProgramming");
  const ret = await deleteWC(card);
  return ret;
};

/* updating current workout card */
export const updateWorkoutCard = async (UID, workoutCardData) => {
  const docRef = doc(firebaseDB, COLLECTIONS.WorkoutCards, UID);
  return await setDoc(docRef, workoutCardData);
};

/*
 * Update programming weeks in Timeline mode
 */
export const updateProgrammingWeeks = async (UID, weeks) => {
  const docRef = doc(firebaseDB, COLLECTIONS.Programmings, UID);
  return await updateDoc(docRef, { weeks: weeks });
};

/* The rules refused the programme read: a PT programme this viewer is not in. */
export class ProgrammeReadDeniedError extends Error {
  constructor(cause) {
    super("This programme is private.", { cause });
    this.name = "ProgrammeReadDeniedError";
    this.code = "permission-denied";
  }
}

export const isProgrammeReadDenied = (err) => err instanceof ProgrammeReadDeniedError;

/*
 * load specific programming; undefined when the document does not exist
 */
export const loadProgrammeWithUID = async (programUID) => {
  const docRef = doc(firebaseDB, COLLECTIONS.Programmings, programUID);
  let docSnap;
  try {
    docSnap = await getDoc(docRef);
  } catch (err) {
    if (err?.code === "permission-denied") throw new ProgrammeReadDeniedError(err);
    throw err;
  }

  if (docSnap.exists()) {
    return { id: docSnap.id, data: docSnap.data() };
  }
  return undefined;
};

export const loadWorkoutCard = async (UID) => {
  const docRef = doc(firebaseDB, COLLECTIONS.WorkoutCards, UID);
  const docSnap = await getDoc(docRef);
  return docSnap;
};

/* load workout cards of specific date range */
export const loadWorkoutCard_DateRange = async (programmingUID, startDate, endDate) => {
  const queryRef = query(
    workoutCollection,
    where("programmingUID", "==", programmingUID),
    where("workoutData.date", ">=", startDate),
    where("workoutData.date", "<=", endDate)
  );

  const querySnapshot = await getDocs(queryRef);
  return querySnapshot;
};

/* load workout cards of specific date range */
export const loadWorkoutCard_week = async (programmingUID, week) => {
  const queryRef = query(
    workoutCollection,
    where("programmingUID", "==", programmingUID),
    where("workoutData.week", "==", week)
  );

  const querySnapshot = await getDocs(queryRef);
  return querySnapshot;
};

/* load all workout cards of specific */
export const loadAllWorkoutCard = async (programmingUID) => {
  const queryRef = query(workoutCollection, where("programmingUID", "==", programmingUID));

  const querySnapshot = await getDocs(queryRef);
  return querySnapshot;
};

/*
 * Update control setting of a programming
 */
export const updateProgrammingControlSetting = async (controlSetting) => {
  const updateControlSetting = httpsCallable(firebaseFunctions, "updateProgrammingControlSettings");
  const ret = await updateControlSetting(controlSetting);
  return ret;
};

/*
 * get list of subscriber UIDs
 */
export const getProgrammingSubscribersUIDs = async (programmingUID) => {
  const getSubscribers = httpsCallable(firebaseFunctions, "getProgrammingSubscribersUIDs");

  const ret = await getSubscribers({ programmingUID: programmingUID });
  return ret;
};

/*
 * Check if user has access to a programming
 */
export const checkProgrammingAccess = async (programmingUID) => {
  const checkAccess = httpsCallable(firebaseFunctions, "checkProgrammingAccess");
  const result = await checkAccess({ programmingUID });
  return result.data.hasAccess;
};

/*
 * Get today's workout for visit card users
 */
export const getVisitCardTodaysWorkout = async (timezoneOffset) => {
  const getWorkout = httpsCallable(firebaseFunctions, "getVisitCardTodaysWorkout");
  const result = await getWorkout({ timezoneOffset });
  return result.data;
};

/*
 * get subscription info of a user for a programming
 */
export const loadUserSubInfoForProgram = async (userUID, programmingUID) => {
  const userRef = doc(
    firebaseDB,
    COLLECTIONS.Users,
    userUID,
    COLLECTIONS.UserLibrary,
    programmingUID
  );
  const docSnap = await getDoc(userRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    return data;
  } else {
    return null;
  }
};

/*
 * get list of subscriber
 */
export const getProgrammingSubscribers = async (programmingUID) => {
  const getSubscribers = httpsCallable(firebaseFunctions, "getProgrammingSubscribers");

  const ret = await getSubscribers({ programmingUID: programmingUID });
  return ret;
};

/*
 * Program members, capped: resolves to { subscribers: [{ uid, name }], total }
 */
export const getProgramMembers = async (programmingUID) => {
  const getSubscribers = httpsCallable(firebaseFunctions, "getProgrammingSubscribers");
  const ret = await getSubscribers({ programmingUID, withTotal: true });
  return ret.data;
};

export const getWorkoutNotes = async (programmingUID, workoutIDs) => {
  const getNotes = httpsCallable(firebaseFunctions, "getWorkoutNotes");
  const ret = await getNotes({ programmingUID, workoutIDs });
  return ret.data;
};

export const saveWorkoutNote = async (programmingUID, workoutID, text) => {
  const saveNote = httpsCallable(firebaseFunctions, "saveWorkoutNote");
  const ret = await saveNote({ programmingUID, workoutID, text });
  return ret.data;
};

/* ===========================================
 * User Management
 * ===========================================
 */
/*
 * set lemonade admin custom claim to the user by email
 */
export const setLemonadeAdminAccessByEmail = async (email) => {
  const assignLemonadeAdmin = httpsCallable(firebaseFunctions, "setLemonadeAdminAccessByEmail");
  const ret = await assignLemonadeAdmin({ email: email });
  return ret;
};

/*
 * set coach custom claim to the user by email
 */
export const setCoachAccessByEmail = async (email) => {
  const assignCoach = httpsCallable(firebaseFunctions, "setCoachAccessByEmail");
  const ret = await assignCoach({ email });
  return ret;
};

/*
 * set box custom claim to the user by email
 */
export const setBoxAccessByEmail = async (email) => {
  const assignBox = httpsCallable(firebaseFunctions, "setBoxAccessByEmail");
  const ret = await assignBox({ email });
  return ret;
};

/*
 * send invitation to the user by email
 */
export const sendInvitationByEmail = async (invitation) => {
  const sendInvitation = httpsCallable(firebaseFunctions, "sendInvitationByEmail");
  const ret = await sendInvitation({ invitation });
  return ret;
};

/*
 * accept or decline invitation
 */
export const acceptOrDeclineInvitation = async (invitationUID, status) => {
  const acceptDecline = httpsCallable(firebaseFunctions, "acceptOrDeclineInvitation");
  const ret = await acceptDecline({ invitationUID, status });
  return ret;
};

/* ===========================================
 * Product Management
 * ===========================================
 */
/*
 * Creating Product
 * @param product: should be of type ProductObj
 */
export const createProduct = async (product, isLemonadeProduct = false) => {
  /* creating the stripe product and price */
  const createProduct = httpsCallable(firebaseFunctions, "createProduct");

  const { data } = await createProduct({ product, isLemonadeProduct });

  // The callable answers with identifiers only — never Stripe SDK objects,
  // whose Decimal fields cannot cross the callable encoder. `productID` is
  // always set; `stripeProductID` carries the same id.
  const productID = data.productID || data.stripeProductID;

  // No silent fallback to null on this seam. Callers treat a returned id as
  // proof of success — one announces "Programming #<id> Created" and navigates
  // to it — so a missing id must surface as the failure it is, not as a
  // success carrying `null`. Their catch blocks turn this into an honest error.
  if (!productID) {
    throw new Error("createProduct returned no product id");
  }

  return productID;
};

/*
 * update product
 * @param productUID: UID of the product
 * @param dataToUpdate: data to update
 */
export const updateProduct = async (productUID, productType, dataToUpdate) => {
  // A linked-programming change grants or revokes every current holder before returning.
  const updateCoverArt = httpsCallable(firebaseFunctions, "updateProduct", { timeout: 540000 });
  return updateCoverArt({ productUID, productType, dataToUpdate });
};

/**
 * Set featured membership for a box
 * @param {string} boxID - Box ID
 * @param {string} membershipUID - Membership product UID to set as featured
 */
export const setFeaturedMembership = async (boxID, membershipUID) => {
  const setFeatured = httpsCallable(firebaseFunctions, "setFeaturedMembership");
  return setFeatured({ boxID, membershipUID });
};

/*
 * update product Cover Art
 * @param productUID: UID of the product
 * @param coverArtURL: URL of the cover art
 */
export const updateProductCoverURL = async (productUID, productType, coverArtURL) => {
  const updateCoverArt = httpsCallable(firebaseFunctions, "updateProductCoverURL");
  return updateCoverArt({ productUID, productType, coverArtURL });
};

/*
 * load specific programming
 */
export const loadCoachProductWithUID = async (productUID) => {
  const docRef = doc(firebaseDB, COLLECTIONS.CoachingSubscriptions, productUID);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, data: docSnap.data() };
  } else {
    //TODO: do the error handling here
    console.log("No such document!");
  }
};

/*
 * Update a membership product's pricing matrix.
 *
 * The server owns everything about the resulting Stripe prices: it mints new
 * ones for changed cells, retires their predecessors, and strips any priceID
 * a client sends. Members already on a plan are never re-priced — an edit
 * provisions prices for FUTURE purchases only.
 *
 * @param productUID: UID of the membership product
 * @param pricingOptions: the full desired cell map. A provisioned cell may
 *                        be deactivated (`active: false`) but never omitted;
 *                        the server refuses a payload that drops one,
 *                        because that price may still have members on it.
 * @param options: { boxID, rollTargetRate, endBehavior } — each optional; an
 *                 omitted field leaves the stored configuration alone
 */
export const updateMembershipPricing = async (productUID, pricingOptions, options = {}) => {
  const updatePricing = httpsCallable(firebaseFunctions, "updateMembershipPricing");
  const payload = {
    productUID,
    productType: PRODUCT_TYPE.Membership,
    pricingOptions,
  };
  if (options.boxID) payload.boxID = options.boxID;
  if (options.rollTargetRate) payload.rollTargetRate = options.rollTargetRate;
  if (options.endBehavior) payload.endBehavior = options.endBehavior;
  return updatePricing(payload);
};

/*
 * Archive Product
 * @param productUID: UID of the product
 * @param productType: Type of the product
 */
export const archiveProduct = async (productUID, productType) => {
  const archive = httpsCallable(firebaseFunctions, "archiveProduct");
  return archive({ productUID, productType });
};

/*
 * Discontinue Product — archives the product AND cancels every active
 * subscription to it at the end of the member's paid period. Archiving alone
 * only stops new sales and leaves existing members untouched.
 * @param productUID: UID of the product
 * @param productType: Type of the product
 * @param boxID: UID of the owning box, when the product belongs to one
 * @param confirm: only `true` cancels anything; anything else is a dry-run
 *                 preview that mutates nothing and returns the affected count
 */
export const discontinueProduct = async (productUID, productType, boxID, confirm) => {
  const discontinue = httpsCallable(firebaseFunctions, "discontinueProduct");
  return discontinue({ productUID, productType, boxID, confirm: confirm === true });
};

/* ===========================================
 * Subscriptions
 * ===========================================
 */
export const registerFreeProgram = async (userUID, programUID, programName) => {
  const register = httpsCallable(firebaseFunctions, "registerFreeProgramming");
  return register({
    userUID: userUID,
    programUID: programUID,
    programName: programName,
  });
};

/*
 * load user subscriptions
 */
export const getUserLibrary = async (userUID) => {
  const load = httpsCallable(firebaseFunctions, "getUserProgrammings");
  return load({ userUID });
};

/*
 * cancel subscription
 * @param subscriptionId: The ID of the subscription object.
 * @param stripeAccountID: The ID of the connected account that the subscription belongs to.
 * stripeAccountID = null means it is Lemonade product
 */
export const cancelSubscription = async (subscriptionId, stripeAccountID = null) => {
  const cancel = httpsCallable(firebaseFunctions, "cancelSubscription");
  return cancel({ subscriptionId, stripeAccountID });
};

/*
 * ===========================================
 * Commitment membership cancellation
 * ===========================================
 *
 * A committed membership never goes through `cancelSubscription` above: that
 * callable talks straight to Stripe, and a term bounded by a Subscription
 * Schedule needs the schedule rewritten instead. These three wrap the lanes
 * that do it. Everything the lanes decide from is re-read server-side from the
 * caller's verified uid — the payload names a membership and is believed about
 * nothing else.
 */

/**
 * Cancel through one lane.
 * @param {import("@lemonadetraining/contracts").SubmitMembershipCancelRequest} payload
 */
export const submitMembershipCancel = async (payload) => {
  const submit = httpsCallable(firebaseFunctions, "submitMembershipCancel");
  return submit(payload);
};

/* Take back a commitment-end cancellation, up until the end date. */
export const undoMembershipCancel = async (subscriptionId, membershipProductUID) => {
  const undo = httpsCallable(firebaseFunctions, "undoMembershipCancel");
  return undo({ subscriptionId, membershipProductUID });
};

/*
 * The guarded account deletion (LT-2003). mode "check" deletes nothing; a
 * refusal is a failed-precondition whose details carry the reason.
 * @param mode: "check" | "delete"
 * @return DeleteMyAccountResponse
 */
export const deleteMyAccount = async (mode) => {
  const call = httpsCallable(firebaseFunctions, "deleteMyAccount");
  const result = await call({ mode });
  return result.data;
};

/* Withdraw one's own still-pending request to the gym. */
export const withdrawMembershipRequest = async (requestId) => {
  const withdraw = httpsCallable(firebaseFunctions, "withdrawMembershipRequest");
  return withdraw({ requestId });
};

/*
 * Opens a Stripe-hosted page for the member to replace the card behind one
 * of their own memberships. The server pins the return URLs; the page never
 * claims success from the redirect back, only from the listener.
 * @param subscriptionId: the member's own Stripe subscription id.
 * @return { id, url }
 */
export const createPaymentMethodUpdateSession = async (subscriptionId) => {
  const create = httpsCallable(firebaseFunctions, "createPaymentMethodUpdateSession");
  const result = await create({ subscriptionId });
  const data = result?.data;
  if (typeof data?.url !== "string" || !data.url.startsWith("https://")) {
    throw new Error("card update page was not returned");
  }
  return { id: data.id, url: data.url };
};

/*
 * What settling this request would cost, computed server-side.
 * Shares a 20-per-minute budget with resolveMembershipRequest, so call it when
 * a gym opens a request — not for every request on screen.
 */
export const previewMembershipRequestSettlement = async (requestId) => {
  const preview = httpsCallable(firebaseFunctions, "previewMembershipRequestSettlement");
  return preview({ requestId });
};

/*
 * The gym's answer to a membership request.
 * @param payload: { requestId, action } where action is "approve"
 *   (+ treatment, confirmedAmountCents), "deny" (+ decisionNote?),
 *   "contest" (+ contestNote?) or "uphold".
 */
export const resolveMembershipRequest = async (payload) => {
  const resolve = httpsCallable(firebaseFunctions, "resolveMembershipRequest");
  return resolve(payload);
};
/* ===========================================
 * Workout Results
 * ===========================================
 */

/*
 * Read workout results
 */
export const loadWorkoutResults = async (workoutUID, userUID) => {
  const load = httpsCallable(firebaseFunctions, "getWorkoutCardScoresWithUID");
  return load({ workoutUID: workoutUID, userUID: userUID });
};

/* ===========================================
 * Customer Landing page
 * ===========================================
 */

/*
 * Check if new landing page name is available (unified for coach and box)
 */
export const checkNewLandingPageName = async (pageName, type, boxID) => {
  // Set defaults
  type = type || "coach";

  const checkNewName = httpsCallable(firebaseFunctions, "checkNewLandingPageName");
  return checkNewName({ pageName, type, boxID: boxID || null });
};

/*
 * Create Landing Page (unified for coach and box)
 */
export const createLandingPageBackend = async (pageName, type, boxID) => {
  // Set defaults
  type = type || "coach";

  const createPage = httpsCallable(firebaseFunctions, "createLandingPage");
  return createPage({ pageName, type, boxID: boxID || null });
};

/*
 * Get Landing Page info (unified for coach and box)
 */
export const getLandingPageData = async (type, boxID) => {
  // Set defaults
  type = type || "coach";

  const getPage = httpsCallable(firebaseFunctions, "getLandingPage");
  return getPage({ type, boxID: boxID || null });
};

/*
 * Update Landing Page info (unified for coach and box)
 */
export const updateLandingPageData = async (info, type, boxID) => {
  // Set defaults
  type = type || "coach";

  const updatePage = httpsCallable(firebaseFunctions, "updateLandingPage");
  return updatePage({ ...info, type, boxID: boxID || null });
};

/*
 * Get Landing Page info
 */
export const getLandingPageDataByName = async (pageName) => {
  const getPage = httpsCallable(firebaseFunctions, "getLandingPageBasedOnName");
  return getPage({ pageName: pageName });
};

/*
 * Get Landing Page programmings
 */
export const getLandingPageProgrammings = async (ownerUID) => {
  const getProgrammings = httpsCallable(firebaseFunctions, "getLandingPageProgrammings");
  return getProgrammings({ ownerUID: ownerUID });
};

/*
 * Upload landing page banner (unified for coach and box)
 * @param {File} file - The image file to upload
 * @param {string} type - "coach" or "box"
 * @param {string|null|undefined} userUID - User UID (required when type is "coach")
 * @param {string|null|undefined} boxID - Box ID (required when type is "box")
 */
export const uploadLandingPageBanner = async (file, type, userUID, boxID) => {
  // Set defaults if not provided
  type = type || "coach";

  let storagePath;

  if (type === "box" && boxID) {
    storagePath = `boxes/${boxID}/landingPageBanner`;
  } else if (type === "coach" && userUID) {
    storagePath = `users/${userUID}/LandingPageBanner`;
  } else {
    throw new Error("Missing required parameters for banner upload");
  }

  return resizeAndUpload(file, "landingBanner", () => storagePath);
};

/* ===========================================
 * Box Landing Page Functions
 * ===========================================
 */

/*
 * Get Box Landing Page Memberships
 */
export const getBoxLandingPageMemberships = async (boxID) => {
  const getMemberships = httpsCallable(firebaseFunctions, "getBoxLandingPageMemberships");
  return getMemberships({ boxID });
};

/*
 * Get Box Landing Page Programmings (not linked to memberships)
 */
export const getBoxLandingPageProgrammings = async (boxID) => {
  const getProgrammings = httpsCallable(firebaseFunctions, "getBoxLandingPageProgrammings");
  return getProgrammings({ boxID });
};

/*
 * Get User Visit Cards
 */
export const getUserVisitCards = async () => {
  const getVisitCards = httpsCallable(firebaseFunctions, "getUserVisitCards");
  return getVisitCards();
};

/* ===========================================
 * Box Management
 * ===========================================
 */
/*
 * Upload box logo
 * @param boxID - Box ID
 * @param file - Image file
 */
export const uploadBoxLogo = async (boxID, file) => {
  if (!file) {
    return "";
  }
  return resizeAndUpload(file, "boxLogo", () => `boxes/${boxID}/logo`);
};

/*
 * Create a new box
 * @param boxData - Box data following Box type structure
 */
export const createBox = async (boxData) => {
  const createBoxFunction = httpsCallable(firebaseFunctions, "createBox");
  const ret = await createBoxFunction(boxData);
  return ret;
};

/*
 * Update a box
 * @param boxID - Box ID
 * @param updateData - Partial box data to update
 */
export const updateBox = async (boxID, updateData) => {
  const updateBoxFunction = httpsCallable(firebaseFunctions, "updateBox");
  const ret = await updateBoxFunction({ boxID, updateData });
  return ret;
};

/*
 * Invite a coach to a box
 * @param boxID - Box ID
 * @param email - Coach email address
 * @param permissions - Optional permissions object
 */
export const inviteCoachToBox = async (boxID, email, permissions) => {
  console.log("inviteCoachToBox request:", boxID, email, permissions);
  const inviteCoachFunction = httpsCallable(firebaseFunctions, "inviteCoachToBox");
  const ret = await inviteCoachFunction({ boxID, email, permissions });
  return ret;
};

/*
 * Update box member permissions
 * @param boxID - Box ID
 * @param userUID - User UID
 * @param permissions - Permissions object
 */
export const updateBoxMemberPermissions = async (boxID, userUID, permissions) => {
  const updatePermissionsFunction = httpsCallable(firebaseFunctions, "updateBoxMemberPermissions");
  const ret = await updatePermissionsFunction({ boxID, userUID, permissions });
  return ret;
};

/*
 * Get box Stripe account status
 * @param boxID - Box ID
 */
export const getBoxStripeAccountStatus = async (boxID) => {
  const getStatusFunction = httpsCallable(firebaseFunctions, "getBoxStripeAccountStatus");
  const ret = await getStatusFunction({ boxID });
  return ret;
};

/*
 * Link Stripe account to box
 * @param boxID - Box ID
 * @param stripeAccountID - Stripe account ID
 */
export const linkStripeAccountToBox = async (boxID, stripeAccountID) => {
  const linkFunction = httpsCallable(firebaseFunctions, "linkStripeAccountToBox");
  const ret = await linkFunction({ boxID, stripeAccountID });
  return ret;
};

/* ===========================================
 * Auth Utilities
 * ===========================================
 */

/*
 * Refresh the current user's auth token to get updated custom claims.
 * Use this after operations that modify custom claims (e.g., createBox)
 * to ensure subsequent requests have the updated permissions.
 * @returns {Promise<string|null>} The new ID token, or null if no user is signed in
 */
/*
 * The gym id in a freshly refreshed token, or null. Tells whether a createBox
 * whose answer was lost actually created the gym.
 * @returns {Promise<string|null>}
 */
export const refreshedClaimsBoxID = async () => {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) {
    return null;
  }
  const result = await currentUser.getIdTokenResult(true);
  return typeof result.claims.boxID === "string" ? result.claims.boxID : null;
};

export const refreshAuthToken = async () => {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) {
    return null;
  }
  // Force refresh to get updated custom claims from server
  const newToken = await currentUser.getIdToken(true);
  return newToken;
};
